import { useApolloClient, useQuery } from '@apollo/client';
import { format } from 'date-fns';
import {
  ArrowLeft,
  ExternalLink,
  ExternalLinkIcon,
  Loader2,
  Upload,
  X,
} from 'lucide-react';
import { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Link, useParams } from 'react-router-dom';

import { GET_ALL_DOME_DOCUMENTS_QUERY } from '@/apollo/schemas/assets/getAllDomeDocuments';
import { GET_ASSET_QUERY } from '@/apollo/schemas/assets/getAsset';
import { GET_DOCUMENT_UPLOAD_URL } from '@/apollo/schemas/assets/getDocumentUploadUrl';
import { ChainAddress } from '@/components/common/ChainAddress';
import { Loader } from '@/components/common/Loader';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/context/CoginitoAuthProvider';
import { chainLabel } from '@/utils/chains';

const AssetDetail = () => {
  const { id } = useParams<{ id: string }>();
  // console.log("ID",id);

  const { session } = useAuth();
  const jwtToken = session
    ? session?.getIdToken().getJwtToken()
    : localStorage.getItem('idToken');

  const client = useApolloClient();

  // State for document upload
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [documentName, setDocumentName] = useState('');
  const [documentType, setDocumentType] = useState('');
  const [description, setDescription] = useState('');
  const [documentDate, setDocumentDate] = useState('');
  const [docCategory, setDocCategory] = useState<'POR' | 'PPM'>('POR');
  const [activeDocTab, setActiveDocTab] = useState<'POR' | 'PPM'>('POR');
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState<string | null>(null);

  const { data, loading, error } = useQuery(GET_ASSET_QUERY, {
    skip: !jwtToken || !id,
    variables: {
      id: id,
    },
    context: {
      headers: {
        identity: jwtToken,
      },
    },
  });

  const asset = data?.getAsset?.asset;

  // Query for fetching documents
  const {
    data: documentsData,
    loading: documentsLoading,
    refetch: refetchDocuments,
  } = useQuery(GET_ALL_DOME_DOCUMENTS_QUERY, {
    skip: !jwtToken || !id,
    variables: {
      assetId: parseInt(id || '0'),
      folderPrefix: activeDocTab === 'PPM' ? 'ppm-documents' : 'dome-documents',
    },
    context: {
      headers: {
        identity: jwtToken,
      },
    },
  });

  const documents = documentsData?.getAllDomeDocuments?.documents || [];

  // Dropzone configuration
  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      setSelectedFile(file);
      // Auto-fill document name from file name (user can edit)
      setDocumentName(file.name);
      // Auto-fill document type from file MIME type (hidden from user)
      setDocumentType(file.type);
      setUploadError(null);
      setUploadSuccess(null);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    maxFiles: 1,
    accept: {
      'application/pdf': ['.pdf'],
      // 'image/*': ['.png', '.jpg', '.jpeg', '.gif'],
      // 'application/msword': ['.doc'],
      // 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
      //   ['.docx'],
    },
  });

  // Handle document upload via presigned URL
  const handleUploadDocument = async () => {
    if (!selectedFile || !documentName || !description || !documentDate) {
      setUploadError('Please fill in all fields and select a file');
      return;
    }

    setUploadLoading(true);
    setUploadError(null);
    setUploadSuccess(null);

    try {
      // Step 1: Get presigned upload URL
      const { data } = await client.mutate({
        mutation: GET_DOCUMENT_UPLOAD_URL,
        variables: {
          input: {
            assetId: parseInt(id || '0'),
            documentName,
            contentType: selectedFile.type,
            date: documentDate,
            folderPrefix:
              docCategory === 'PPM' ? 'ppm-documents' : 'dome-documents',
          },
        },
        context: {
          headers: {
            identity: jwtToken,
          },
        },
      });

      const result = data?.getDocumentUploadUrl;

      if (!result || result.statusCode !== 200) {
        setUploadError(result?.message || 'Failed to get upload URL');
        return;
      }

      // Step 2: Upload file directly to S3 using presigned URL
      const uploadResponse = await fetch(result.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': result.contentType },
        body: selectedFile,
      });

      if (!uploadResponse.ok) {
        setUploadError('Failed to upload file to storage');
        return;
      }

      setUploadSuccess('Document uploaded successfully!');
      // Reset form
      setSelectedFile(null);
      setDocumentName('');
      setDocumentType('');
      setDescription('');
      setDocumentDate('');
      setDocCategory('POR');
      // Refetch documents to update the list
      refetchDocuments();
    } catch (err: any) {
      setUploadError(err.message || 'Upload failed');
      console.error(err);
    } finally {
      setUploadLoading(false);
    }
  };

  // Clear selected file
  const handleClearFile = () => {
    setSelectedFile(null);
    setDocumentName('');
    setDocumentType('');
    setDescription('');
    setDocumentDate('');
    setDocCategory('POR');
    setUploadError(null);
    setUploadSuccess(null);
  };

  if (loading) {
    return <Loader show />;
  }

  if (error || !asset) {
    return (
      <div className="flex flex-col items-center justify-center h-screen">
        <h1 className="text-2xl font-bold mb-4">Asset Not Found</h1>
        <Link to="/assets">
          <Button>
            <ArrowLeft size={16} className="mr-2" />
            Back to Assets
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <main className="h-full p-6">
      <div className="mb-6">
        <Link to="/assets">
          <Button variant="outline">
            <ArrowLeft size={16} className="mr-2" />
            Back to Assets
          </Button>
        </Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Left Column - Asset Details & Documents (3 cols) */}
        <div className="lg:col-span-3 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-2xl">Asset Details</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4">
              {/* Asset Image Preview */}
              {/* {asset.imageUrl && (
                <div className="mb-4">
                  <p className="text-sm text-gray-500 mb-2">Asset Image</p>
                  <div className="rounded-lg border overflow-hidden bg-gray-50">
                    <img
                      src={asset.imageUrl}
                      alt={asset.name}
                      onError={(e) => {
                        e.currentTarget.src = '/images/placeholder.avif';
                      }}
                      className="w-full h-auto max-h-44 object-contain"
                    />
                  </div>
                </div>
              )} */}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Asset ID</p>
                  <p className="text-sm font-semibold">{asset.assetId}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Name</p>
                  <p className="text-sm font-semibold">{asset.name}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Type</p>
                  <p className="text-sm font-semibold">{asset.type}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Status</p>
                  <p className="text-sm font-semibold">
                    <span
                      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                        asset.isActive
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {asset.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Chain</p>
                  <p className="text-sm font-semibold">
                    <span className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium">
                      {chainLabel(asset.chain)}
                    </span>
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Token Standard</p>
                  <p className="text-sm font-semibold">
                    {asset.tokenStandard ?? 'ERC3643'}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Eligible investors</p>
                  {/* Fixed at creation. Accredited-only adds the ACE accredited policy
                      ahead of KYC; it is enforced on-chain once the ACE target is ACTIVE. */}
                  <p className="text-sm font-semibold">
                    {asset.requireAccreditedInvestor
                      ? 'Accredited investors only'
                      : 'Any KYC-verified investor'}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">NFT Token ID</p>
                  <p
                    className="text-sm font-semibold truncate"
                    title={asset.nftTokenId || 'Not minted'}
                  >
                    {asset.nftTokenId || 'Not minted'}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Gold Weight</p>
                  <p className="text-sm font-semibold">
                    {asset.gold_weight} oz
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Copper Weight</p>
                  <p className="text-sm font-semibold">
                    {asset.copper_weight} lbs
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Copper Price</p>
                  <p className="text-sm font-semibold">
                    {asset.copper_price
                      ? new Intl.NumberFormat('en-US', {
                          style: 'currency',
                          currency: 'USD',
                        }).format(asset.copper_price)
                      : 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">
                    Minimum Investment Amount
                  </p>
                  <p className="text-sm font-semibold">
                    {asset.minimumInvestmentAmount
                      ? new Intl.NumberFormat('en-US', {
                          style: 'currency',
                          currency: 'USD',
                        }).format(asset.minimumInvestmentAmount)
                      : 'N/A'}
                  </p>
                </div>
              </div>

              {/* NAV Information */}
              <div className="pt-4 border-t">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">
                      Net Asset Value (NAV)
                    </p>
                    <p className="text-lg font-bold text-green-600">
                      {asset.nav
                        ? new Intl.NumberFormat('en-US', {
                            style: 'currency',
                            currency: 'USD',
                          }).format(asset.nav)
                        : 'Not available'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">NAV Last Updated</p>
                    <p className="text-sm font-semibold">
                      {asset.navLastUpdated
                        ? format(
                            new Date(asset.navLastUpdated),
                            'MMM dd, yyyy HH:mm',
                          )
                        : 'N/A'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Token Information */}
              <div className="pt-4 border-t">
                <p className="text-sm text-gray-500 mb-3 font-semibold">
                  Token Information
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-500">Price Per Token</p>
                    <p className="text-sm font-semibold">
                      {asset.pricePerToken
                        ? new Intl.NumberFormat('en-US', {
                            style: 'currency',
                            currency: 'USD',
                          }).format(asset.pricePerToken)
                        : 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Total Tokens</p>
                    <p className="text-sm font-semibold">
                      {asset.totalTokens?.toLocaleString() ?? 'N/A'}
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 mt-3">
                  <div>
                    <p className="text-xs text-gray-500">Available Tokens</p>
                    <p className="text-sm font-semibold">
                      {asset.availableTokens?.toLocaleString() ?? 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Total Minted Tokens</p>
                    <p className="text-sm font-semibold">
                      {asset.totalMintedTokens?.toLocaleString() ?? 'N/A'}
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 mt-3">
                  <div>
                    <p className="text-xs text-gray-500">
                      Tokenized Percentage
                    </p>
                    <p className="text-sm font-semibold">
                      {asset.tokenizedPercentage != null
                        ? `${asset.tokenizedPercentage}%`
                        : 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Stage Multiplier</p>
                    <p className="text-sm font-semibold">
                      {asset.stageMultiplier ?? 'N/A'}
                    </p>
                  </div>
                </div>
              </div>

              {asset.erc3643 && (
                <div className="pt-4 border-t">
                  <p className="text-sm text-gray-500 mb-3 font-semibold">
                    ERC-3643 Token Information
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-gray-500">Token Name</p>
                      <p className="text-sm font-semibold">
                        {asset.erc3643.name}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Token Symbol</p>
                      <p className="text-sm font-semibold">
                        {asset.erc3643.symbol}
                      </p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mt-3">
                    <div>
                      <p className="text-xs text-gray-500">Token Symbol</p>
                      <p className="text-sm font-semibold flex items-center gap-2">
                        {asset.erc3643.tokenAddress && (
                          <ChainAddress
                            address={
                              asset.tokenRef ?? asset.erc3643.tokenAddress
                            }
                            chain={asset.chain}
                            href={asset.explorerUrl}
                          />
                        )}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Total Supply</p>
                      <p className="text-sm font-semibold">
                        {asset.erc3643.totalSupply}
                      </p>
                    </div>
                  </div>
                  {/* ACE enforcement. A token whose PolicyEngine never attached reverts on
                      every transfer and mint while looking healthy everywhere else, so this
                      is the only place that failure is visible. */}
                  <div className="grid grid-cols-2 gap-4 mt-3">
                    <div>
                      <p className="text-xs text-gray-500">ACE enforcement</p>
                      <p className="text-sm font-semibold">
                        {!asset.erc3643.compliance ? (
                          <span className="text-gray-500">Unknown</span>
                        ) : asset.erc3643.compliance.engine !== 'ACE_ONLY' ? (
                          <span className="text-rose-600">
                            Not enforced - no PolicyEngine attached
                          </span>
                        ) : asset.erc3643.compliance.aceTargetStatus !==
                          'ACTIVE' ? (
                          <span className="text-amber-600">
                            {asset.erc3643.compliance.aceTargetStatus ??
                              'Not registered'}
                          </span>
                        ) : (
                          <span className="text-emerald-600">Enforced</span>
                        )}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Gated methods</p>
                      <p
                        className="text-sm font-semibold truncate"
                        title={
                          asset.erc3643.compliance?.protectedMethods?.join(
                            ', ',
                          ) || undefined
                        }
                      >
                        {asset.erc3643.compliance?.protectedMethods?.length
                          ? asset.erc3643.compliance.protectedMethods.length
                          : 'None'}
                      </p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mt-3">
                    <div>
                      <p className="text-xs text-gray-500">Decimals</p>
                      <p className="text-sm font-semibold">
                        {asset.erc3643.decimals ?? 'N/A'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Created At</p>
                      <p className="text-sm font-semibold">
                        {asset.erc3643.createdAt
                          ? format(
                              new Date(asset.erc3643.createdAt),
                              'MMM dd, yyyy',
                            )
                          : 'N/A'}
                      </p>
                    </div>
                  </div>
                  <div className="mt-3">
                    <p className="text-xs text-gray-500">Owner</p>
                    <p className="text-sm font-semibold flex items-center gap-2">
                      <ChainAddress
                        address={asset.erc3643.owner}
                        chain={asset.chain}
                      />
                    </p>
                  </div>
                  {asset.erc3643.identityRegistry && (
                    <div className="mt-3">
                      <p className="text-xs text-gray-500">Identity Registry</p>
                      <p className="text-sm font-semibold flex items-center gap-2">
                        <ChainAddress
                          address={asset.erc3643.identityRegistry}
                          chain={asset.chain}
                        />
                      </p>
                    </div>
                  )}
                </div>
              )}

              <div>
                <p className="text-sm text-gray-500 mb-2">Certificate</p>
                <a
                  href={asset.certificateUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline flex items-center gap-2"
                >
                  View Certificate
                  <ExternalLink size={16} />
                </a>
              </div>

              <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                <div>
                  <p className="text-sm text-gray-500">Created At</p>
                  <p className="text-base">
                    {asset.createdAt
                      ? format(new Date(asset.createdAt), 'MMM dd, yyyy HH:mm')
                      : 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Updated At</p>
                  <p className="text-base">
                    {asset.updatedAt
                      ? format(new Date(asset.updatedAt), 'MMM dd, yyyy HH:mm')
                      : 'N/A'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* NFTs List */}
          {asset.nfts && asset.nfts.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Asset NFTs</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {asset.nfts.map((nft: any) => (
                    <div
                      key={nft.id}
                      className="p-4 bg-gray-50 rounded-lg border hover:bg-gray-100 transition-colors"
                    >
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-xs text-gray-500">Token ID</p>
                          <p
                            className="text-sm font-semibold truncate"
                            title={nft.tokenId}
                          >
                            {nft.tokenId}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Owner</p>
                          <p
                            className="text-sm font-semibold truncate"
                            title={nft.owner}
                          >
                            {nft.owner
                              ? `${nft.owner.substring(0, 8)}...${nft.owner.substring(nft.owner.length - 4)}`
                              : 'N/A'}
                          </p>
                        </div>
                      </div>
                      {nft.metadata && (
                        <div className="mt-2">
                          <p className="text-xs text-gray-500">Metadata</p>
                          <p className="text-sm font-mono bg-gray-100 p-2 rounded mt-1 break-all">
                            {typeof nft.metadata === 'string'
                              ? nft.metadata
                              : JSON.stringify(nft.metadata)}
                          </p>
                        </div>
                      )}
                      <div className="mt-2">
                        <p className="text-xs text-gray-400">
                          Created:{' '}
                          {nft.createdAt
                            ? format(
                                new Date(nft.createdAt),
                                'MMM dd, yyyy HH:mm',
                              )
                            : 'N/A'}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Uploaded Documents List */}
          <Card>
            <CardHeader>
              <CardTitle>Asset Documents</CardTitle>
            </CardHeader>
            <CardContent>
              <Tabs
                value={activeDocTab}
                onValueChange={(val) => {
                  setActiveDocTab(val as 'POR' | 'PPM');
                  refetchDocuments({
                    assetId: parseInt(id || '0'),
                    folderPrefix:
                      val === 'PPM' ? 'ppm-documents' : 'dome-documents',
                  });
                }}
              >
                <TabsList className="mb-4">
                  <TabsTrigger value="POR">POR</TabsTrigger>
                  <TabsTrigger value="PPM">PPM</TabsTrigger>
                </TabsList>

                {(['POR', 'PPM'] as const).map((tab) => (
                  <TabsContent key={tab} value={tab}>
                    {documentsLoading ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
                      </div>
                    ) : documents.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <p>No {tab} documents uploaded yet</p>
                        <p className="text-sm mt-2">
                          Upload a document to get started
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {documents.map((doc: any) => (
                          <div
                            key={doc.id}
                            className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border hover:bg-gray-100 transition-colors"
                          >
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              <Upload className="h-5 w-5 text-blue-500 flex-shrink-0" />
                              <div className="flex-1 min-w-0">
                                <p className="font-medium truncate">
                                  {doc.documentName}
                                </p>
                                <p className="text-sm text-gray-500 truncate">
                                  {doc.description}
                                </p>
                                <p className="text-xs text-gray-400 mt-1">
                                  {format(
                                    new Date(doc.createdAt),
                                    'MMM dd, yyyy HH:mm',
                                  )}
                                </p>
                              </div>
                            </div>
                            <a
                              href={doc.documentUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800 flex-shrink-0 ml-3"
                              title="View Document"
                            >
                              <ExternalLink className="h-5 w-5" />
                            </a>
                          </div>
                        ))}
                      </div>
                    )}
                  </TabsContent>
                ))}
              </Tabs>
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Upload Section (2 cols) */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-2xl">Upload Documents</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4">
              {/* Dropzone */}
              <div
                {...getRootProps()}
                className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                  isDragActive
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                <input {...getInputProps()} />
                <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                {isDragActive ? (
                  <p className="text-blue-500">Drop the file here...</p>
                ) : (
                  <div>
                    <p className="text-gray-600 mb-2">
                      Drag and drop a file here, or click to select
                    </p>
                    <p className="text-sm text-gray-400">
                      Supported: PDF, Images, DOC, DOCX
                    </p>
                  </div>
                )}
              </div>

              {/* Selected File Display */}
              {selectedFile && (
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Upload className="h-6 w-6 text-blue-500" />
                    <div>
                      <p className="font-medium">{selectedFile.name}</p>
                      <p className="text-sm text-gray-500">
                        {(selectedFile.size / 1024).toFixed(2)} KB •{' '}
                        {documentType}
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleClearFile}
                    disabled={uploadLoading}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              )}

              {/* Form Fields */}
              <div className="grid gap-4">
                <div>
                  <Label htmlFor="docCategory">Document Type</Label>
                  <Select
                    value={docCategory}
                    onValueChange={(val) =>
                      setDocCategory(val as 'POR' | 'PPM')
                    }
                    disabled={uploadLoading}
                  >
                    <SelectTrigger id="docCategory">
                      <SelectValue placeholder="Select document type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="POR">POR</SelectItem>
                      <SelectItem value="PPM">PPM</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="documentName">Document Name</Label>
                  <Input
                    id="documentName"
                    value={documentName}
                    onChange={(e) => setDocumentName(e.target.value)}
                    placeholder="Enter document name"
                    disabled={uploadLoading}
                  />
                </div>

                <div>
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Enter document description"
                    rows={3}
                    disabled={uploadLoading}
                  />
                </div>

                <div>
                  <Label htmlFor="documentDate">Date</Label>
                  <Input
                    id="documentDate"
                    type="date"
                    value={documentDate}
                    onChange={(e) => setDocumentDate(e.target.value)}
                    disabled={uploadLoading}
                  />
                </div>
              </div>

              {/* Error Message */}
              {uploadError && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-600">
                  {uploadError}
                </div>
              )}

              {/* Success Message */}
              {uploadSuccess && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-green-600">
                  {uploadSuccess}
                </div>
              )}

              {/* Upload Button */}
              <Button
                onClick={handleUploadDocument}
                disabled={!selectedFile || uploadLoading}
                className="w-full"
              >
                {uploadLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="mr-2 h-4 w-4" />
                    Upload Document
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </main>
  );
};

export default AssetDetail;
